import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import AcceptInvite from './AcceptInvite';
import AiAgentAdminDashboard from '@/client/components/admin/ai-agent/routes/AiAgentAdminDashboard';
import AiAgentAdminLayout from '@/client/components/admin/ai-agent/routes/AiAgentAdminLayout';
import App from '../App';
import DesktopOnlyLayout from '../components/layout/admin/DesktopOnlyLayout';
import ErrorScreen from './ErrorScreen';
import ExpertDetails from '@/client/components/admin/ai-agent/experts/routes/ExpertDetails';
import ExpertsOverview from '@/client/components/admin/ai-agent/experts/routes/ExpertsOverview';
import InactiveRegistrationFormPage from './InactiveRegistrationFormPage';
import LoggedInLayout from '../components/layout/LoggedInLayout';
import FullScreenAdminLayout from '@/client/components/layout/admin/FullScreenAdminLayout';
import LoggedOutFullScreenLayout from '../components/layout/LoggedOutFullScreenLayout';
import LoggedOutLayout from '../components/layout/LoggedOutLayout';
import HubTransition from './HubTransition';
import McpConsent from './McpConsent';
import OauthTransition from './OauthTransition';
import SamlTransition from './SamlTransition';
import SamlTransitionError from './SamlTransitionError';
import SiteRoot from './SiteRoot';
import Suspended from './Suspended';
import WaitForInvite from './WaitForInvite';

// Admin layout wrappers
const AdminAssignmentsLayout = lazy(
  () => import('../components/admin/assignments/routes/AdminAssignmentsLayout'),
);
const AdminConnectLayout = lazy(() => import('../components/layout/admin/AdminConnectLayout'));
const AdminContentAuthoringLayout = lazy(
  () => import('@/client/components/admin/layout/AdminContentAuthoringLayout'),
);
const HubLayout = lazy(() => import('../components/admin/hubs/HubLayout'));
const HubOrganizations = lazy(() => import('../components/admin/hubs/routes/HubOrganizations'));
const HubReporting = lazy(() => import('../components/admin/hubs/reporting/HubReporting'));
const HubContentLibrary = lazy(
  () => import('../components/admin/hubs/content-library/HubContentLibrary'),
);
const HubAdmins = lazy(() => import('../components/admin/hubs/admins/HubAdmins'));
const AdminCourseLayout = lazy(
  () => import('../components/admin/layout/route-wrappers/AdminCourseLayout'),
);
const AdminJourneyLayout = lazy(
  () => import('../components/admin/layout/route-wrappers/AdminJourneyLayout'),
);
const AdminAllowanceLayout = lazy(() => import('../components/layout/admin/AdminAllowanceLayout'));
const AdminUsersLayout = lazy(
  () => import('../components/admin/layout/route-wrappers/AdminUsersLayout'),
);
const EditAssignmentLayout = lazy(
  () => import('@/client/components/admin/assignments/routes/EditAssignmentLayout'),
);

// Admin routes
const AdminUtilities = lazy(() => import('./admin/AdminUtilities'));
const AdminUserAdd = lazy(() => import('./admin/users/AdminUserAdd'));
const AdminUserBulkAdd = lazy(() => import('./admin/users/AdminUserBulkAdd'));
const AdminUserOverview = lazy(() => import('./admin/users/AdminUserOverview'));
const Categories = lazy(() => import('./admin/connect/Categories'));
const Departments = lazy(() => import('./admin/connect/Departments'));
const DeveloperTools = lazy(() => import('./admin/developer-tools/DeveloperTools'));
const EditArticle = lazy(() => import('./admin/create/content/EditArticle'));
const EditAssessment = lazy(() => import('./admin/create/content/assessments/EditAssessment'));
const EditAssignment = lazy(() => import('../components/admin/assignments/routes/EditAssignment'));
const EditCourse = lazy(() => import('./admin/create/content/tracks/EditCourse'));
const EditJourney = lazy(() => import('./admin/create/content/journeys/EditJourney'));
// JourneysOverview was retired in DEV-13563 — `/admin/journeys` now
// redirects to `/admin/content`.
const ContentOverview = lazy(
  () => import('../components/admin/content/routes/ContentOverview'),
);
const ContentAnalyticsPage = lazy(
  () => import('../components/admin/content-analytics/ContentAnalyticsPage'),
);
const AssessmentAnalyticsPage = lazy(
  () => import('../components/admin/assessment-analytics/AssessmentAnalyticsPage'),
);
const EditFile = lazy(() => import('./admin/create/content/EditFile'));
const EditScorm = lazy(() => import('./admin/create/content/EditScorm'));
const EditVideo = lazy(() => import('./admin/create/content/EditVideo'));
const FeedActivity = lazy(() => import('./admin/users/sftp-feeds/FeedActivity'));
const Grades = lazy(() => import('./admin/connect/Grades'));
const GroupForm = lazy(() => import('./admin/connect/groups/GroupForm'));
const GroupUsers = lazy(() => import('./admin/connect/groups/GroupUsers'));
const Groups = lazy(() => import('./admin/connect/groups/Groups'));
const Levels = lazy(() => import('./admin/connect/Levels'));
const Locations = lazy(() => import('./admin/connect/Locations'));
const PartnersEditor = lazy(() => import('./partners/PartnersEditor'));
const PartnersList = lazy(() => import('./partners/PartnersList'));
const ProductEditor = lazy(() => import('./admin/content-creation/ProductEditor'));
const ProfileForm = lazy(() => import('./admin/profiles/forms/ProfileForm'));
const Profiles = lazy(() => import('./admin/profiles/Profiles'));
const Reports = lazy(() => import('./admin/reports/Reports'));
const ReportsForm = lazy(() => import('./admin/reports/ReportsForm'));
const ContinuInsights = lazy(() => import('./admin/reports/ContinuInsights'));
const ContinuInsightsUnsubscribe = lazy(() => import('./admin/reports/ContinuInsightsUnsubscribe'));
const SftpFeeds = lazy(() => import('./admin/users/sftp-feeds/SftpFeeds'));
const Skills = lazy(() => import('./admin/connect/skills/Skills'));
const Teams = lazy(() => import('./admin/connect/Teams'));
const Theme = lazy(() => import('./admin/theme/Theme'));
const TrackStart = lazy(() => import('./admin/create/content/tracks/TrackStart'));
const UserManagement = lazy(() => import('./UserManagement'));
const UserSeats = lazy(() => import('./admin/users/UserSeats'));
const RegistrationFormsEditor = lazy(() => import('./registration-forms/RegistrationFormsEditor'));
const RegistrationFormsTable = lazy(() => import('./registration-forms/RegistrationFormsTable'));
const ProductLandingPage = lazy(() => import('./registration-forms/ProductLandingPage'));
const CompanyCustomFieldsEditor = lazy(
  () => import('./CompanyCustomFields/CompanyCustomFieldsEditor'),
);
const AssignmentDetails = lazy(
  () => import('@/client/components/admin/assignments/routes/AssignmentDetails'),
);
const AnnouncementsOverview = lazy(
  () => import('@/client/components/admin/messaging/routes/AnnouncementsOverview'),
);
const EditAnnouncement = lazy(
  () => import('@/client/components/admin/messaging/routes/EditAnnouncement'),
);
const AssignmentsOverview = lazy(
  () => import('../components/admin/assignments/routes/overview/AssignmentsOverview'),
);
const AssignmentsOverviewAllDirectAutomated = lazy(
  () =>
    import(
      '@/client/components/admin/assignments/routes/overview/AssignmentsOverviewAllDirectAutomated'
    ),
);
const AssignmentsOverviewUserAssignments = lazy(
  () =>
    import(
      '@/client/components/admin/assignments/routes/overview/user-assignments/AssignmentsOverviewUserAssignments'
    ),
);
const TeamsAppRedirect = lazy(() => import('./admin/TeamsAppRedirect'));

// Surveys routes
const SurveysLayout = lazy(() => import('../components/admin/surveys/routes/SurveysLayout'));
const SurveysOverview = lazy(() => import('../components/admin/surveys/routes/SurveysOverview'));
const EditSurvey = lazy(() => import('../components/admin/surveys/routes/EditSurvey'));
const SurveyHostView = lazy(() => import('../components/admin/surveys/routes/SurveyHostView'));

// Learner Routes
const LearnerDashboardLayout = lazy(
  () => import('../components/learners/dashboard/LearnerDashboardLayout'),
);
const LearnerDashboardOverview = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardOverview'),
);
const LearnerDashboardAssignments = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardAssignments'),
);
const LearnerDashboardBookmarks = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardBookmarks'),
);
const LearnerDashboardRecentlyViewed = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardRecentlyViewed'),
);
const LearnerDashboardRecommendations = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardRecommendations'),
);
const LearnerDashboardWorkshops = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardWorkshops'),
);
const LearnerDashboardSubscriptions = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardSubscriptions'),
);
const LearnerDashboardAchievements = lazy(
  () => import('../components/learners/dashboard/pages/LearnerDashboardAchievements'),
);
const GroupedAssignmentDetails = lazy(
  () => import('../components/learners/dashboard/pages/GroupedAssignmentDetails'),
);
const LearnerProfile = lazy(() => import('@/client/components/learners/profile/LearnerProfile'));
const People = lazy(() => import('./People'));
const DedicatedWorkshops = lazy(() => import('./DedicatedWorkshops'));

// Content Routes
const AssessmentView = lazy(() => import('./content/assessments/AssessmentView'));
const ContentWrapper = lazy(() => import('../components/content/ContentWrapper'));

// Preauth Routes
const Login = lazy(() => import('./Login'));
const Register = lazy(() => import('./Register'));
const RegisterWithCode = lazy(() => import('./RegisterWithCode'));
const RegisterWithUserId = lazy(() => import('./RegisterWithUserId'));
const RegisterAskForCode = lazy(() => import('./RegisterAskForCode'));
const ForgotPassword = lazy(() => import('./ForgotPassword'));
const ResetPassword = lazy(() => import('./ResetPassword'));

// Partner Routes
const PartnersManager = lazy(() => import('./partners/PartnersManager'));
const PartnerManagerContentList = lazy(() => import('./partners/PartnerManagerContentList'));
const PartnerUsersList = lazy(() => import('./partners/PartnerUsersList'));
const PartnerManagerAssignmentsList = lazy(
  () => import('./partners/PartnerManagerAssignmentsList'),
);
const PartnerManagerContentAssign = lazy(() => import('./partners/PartnersManagerContentAssign'));
const PartnerManagerContentActivity = lazy(
  () => import('./partners/PartnerManagerContentActivity'),
);
const PartnersFeatureWrapper = lazy(() => import('./partners/PartnersFeatureWrapper'));
const PartnersPrincipalWrapper = lazy(() => import('./partners/PartnersPrincipalWrapper'));

const Explore = lazy(() => import('./Explore'));
const LegacyExploreRoute = lazy(() => import('./LegacyExploreRoute'));
const AIAgent = lazy(() => import('../components/ai-agent/AIAgent'));
const Product = lazy(() => import('./content/ecommerce/Product'));
const ProductOverview = lazy(() => import('./products/ProductOverview'));
const PaymentWrapper = lazy(() => import('./content/ecommerce/PaymentWrapper'));
const PaymentSetupSuccess = lazy(() => import('./content/ecommerce/PaymentSetupSuccess'));
const PaymentMethodUpdateSuccess = lazy(
  () => import('./content/ecommerce/PaymentMethodUpdateSuccess'),
);
const StripeSetupSuccess = lazy(() => import('./content/ecommerce/StripeSetupSuccess'));
const SubscriptionManagement = lazy(
  () => import('./products/subscriptions/SubscriptionManagement'),
);
const ChangeSubscription = lazy(() => import('./products/subscriptions/ChangeSubscription'));
const CancelSubscription = lazy(() => import('./products/subscriptions/CancelSubscription'));
const PaymentsList = lazy(() => import('./products/paymentsList'));
const Qooper = lazy(() => import('./Qooper'));
const MeetsRedirect = lazy(() => import('./MeetsRedirect'));
const TeamsRedirect = lazy(() => import('./Teams-Redirect'));
const GoogleRedirect = lazy(() => import('./GoogleRedirect'));
const ZoomRedirect = lazy(() => import('./ZoomRedirect'));
const SlackRedirect = lazy(() => import('./SlackRedirect'));
// IconGallery already lazy - keep as is
const AutoAttendWorkshopWrapper = lazy(
  () => import('../components/automated-workshops/routes/AutoAttendWorkshopWrapper'),
);
const AutoAttendWorkshop = lazy(
  () => import('../components/automated-workshops/routes/AutoAttendWorkshop'),
);

const IconGallery = lazy(() => import('./IconGallery'));

const IntegrationsRoute = lazy(
  () => import('../components/admin/integrations/IntegrationsRoute'),
);
// Admin Academies Routes
const CanvasFrame = lazy(
  () => import('@/client/components/admin/academies/builder/canvas/frame/CanvasFrame')
);
const AcademiesLayout = lazy(
  () => import('@/client/components/admin/academies/routes/AcademiesLayout')
);
const AcademiesIndex = lazy(() => import('@/client/components/admin/academies/routes/AcademiesIndex'));
const AcademyConsoleLayout = lazy(
  () => import('@/client/components/admin/academies/console/AcademyConsoleLayout')
);
const AcademyOverview = lazy(
  () => import('@/client/components/admin/academies/routes/console/AcademyOverview')
);
const HeaderFooterSettings = lazy(
  () => import('@/client/components/admin/academies/routes/console/header-footer/HeaderFooterSettings')
);
const BrandingSettings = lazy(
  () => import('@/client/components/admin/academies/routes/console/branding/BrandingSettings')
);
const SeoSettings = lazy(
  () => import('@/client/components/admin/academies/routes/console/seo/SeoSettings')
);
const DomainSettings = lazy(
  () => import('@/client/components/admin/academies/routes/console/domain/DomainSettings')
);
const SiteBuilder = lazy(() => import('@/client/components/admin/academies/routes/SiteBuilder'));

// Admin Badges Routes
const BadgesLayout = lazy(() => import('@/client/components/admin/badges/routes/BadgesLayout'));
const BadgesOverview = lazy(() => import('@/client/components/admin/badges/routes/BadgesOverview'));
const EditBadge = lazy(() => import('@/client/components/admin/badges/routes/EditBadge'));

// Admin Certificates Routes
const CertificatesLayout = lazy(
  () => import('@/client/components/admin/certificates/routes/CertificatesLayout'),
);
const CertificatesOverview = lazy(
  () => import('@/client/components/admin/certificates/routes/CertificatesOverview'),
);
const CertificateWizard = lazy(
  () => import('@/client/components/admin/certificates/routes/CertificateWizard'),
);
const CertificateAnalyticsPage = lazy(
  () => import('@/client/components/admin/certificates/routes/CertificateAnalyticsPage'),
);

// Admin Workshops Routes
const WorkshopsLayout = lazy(
  () => import('../components/admin/workshops/routes/WorkshopsLayout')
);
const WorkshopsOverview = lazy(
  () => import('../components/admin/workshops/routes/WorkshopsOverview')
);
const WorkshopWizard = lazy(
  () => import('../components/admin/workshops/routes/WorkshopWizard')
);
const WorkshopHostView = lazy(
  () => import('../components/admin/workshops/routes/WorkshopHostView')
);
const WorkshopInstanceHostView = lazy(
  () => import('../components/admin/workshops/routes/WorkshopInstanceHostView')
);
const WorkshopSessionHostView = lazy(
  () => import('../components/admin/workshops/routes/WorkshopSessionHostView')
);
const WorkshopCohortHostView = lazy(
  () => import('../components/admin/workshops/routes/WorkshopCohortHostView')
);

// Learner Workshop Detail Route (flag-aware: new page vs pre-migration views)
const LearnerWorkshopRoute = lazy(
  () => import('../components/content/workshop/LearnerWorkshopRoute')
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* The builder's canvas iframe. Deliberately outside <App />: it must
          boot without ChakraProvider (whose global reset would restyle the
          academy blocks), the config fetch, or any admin store — everything
          it renders arrives over the postMessage bridge from the host.
          Outside <App /> also means outside the auth gate every other
          /admin/* route inherits, so this path is reachable with no session.
          That is only safe while the frame fetches nothing: keep it free of
          queries, service calls and store reads — anything it displays must
          keep arriving over the bridge. */}
      <Route
        path="/admin/academies/canvas-frame"
        element={
          <Suspense fallback={null}>
            <CanvasFrame />
          </Suspense>
        }
      />
    <Route path="/" errorElement={<ErrorScreen />} element={<App />}>
      <Route path="" element={<SiteRoot />} />
      <Route path="suspended" element={<Suspended />} />
      <Route path="saml-transition" element={<SamlTransition />} />
      <Route path="saml-transition-error" element={<SamlTransitionError />} />
      <Route path="wait-for-invite" element={<WaitForInvite />} />
      <Route path="page-inactive" element={<InactiveRegistrationFormPage />} />
      <Route path="oauth-transition" element={<OauthTransition />} />
      <Route path="hub-transition" element={<HubTransition />} />
      {/* MCP OAuth consent — no layout, owns its own login round-trip */}
      <Route path="mcp/consent" element={<McpConsent />} />

      <Route element={<LoggedOutLayout />}>
        <Route path="login/extend/:partner" element={<Login />} />
        <Route path="login" element={<Login />} />
        <Route path="forgot" element={<ForgotPassword />} />
        <Route path="register" element={<Register />} />
        <Route path="register-with-userid" element={<RegisterWithUserId />} />
        <Route path="registration/" element={<RegisterAskForCode />} />
        <Route path="registration/:code" element={<RegisterWithCode />} />
        <Route path="accept" element={<AcceptInvite />} />
        <Route path="reset" element={<ResetPassword />} />
      </Route>

      <Route element={<LoggedOutFullScreenLayout />}>
        <Route path="product/registration/:code" element={<RegisterWithCode />} />
      </Route>

      {/* Builder half of the academies route family; the index/console half is
          the matching admin/academies block under DesktopOnlyLayout below. */}
      <Route element={<FullScreenAdminLayout />}>
        <Route path="admin/academies" element={<AcademiesLayout />}>
          <Route path=":academyId/builder" element={<SiteBuilder />} />
        </Route>
      </Route>

      <Route element={<LoggedInLayout />}>
        <Route
          path="overview/settings/integrations/msteams-success"
          element={<TeamsAppRedirect />}
        />
        <Route path="admin/utilities" element={<AdminUtilities />} />
        <Route path="ai-agent" element={<AIAgent />} />
        <Route path="explore/:categoryId" element={<LegacyExploreRoute />} />
        <Route path="explore" element={<Explore />} />
        <Route path="dashboard" element={<LearnerDashboardLayout />}>
          <Route index element={<LearnerDashboardOverview />} />
          <Route path="assignments" element={<LearnerDashboardAssignments />} />
          <Route path="assignments/assignment-details" element={<GroupedAssignmentDetails />} />
          <Route path="bookmarks" element={<LearnerDashboardBookmarks />} />
          <Route path="recently-viewed" element={<LearnerDashboardRecentlyViewed />} />
          <Route path="recommendations" element={<LearnerDashboardRecommendations />} />
          <Route path="workshops" element={<LearnerDashboardWorkshops />} />
          <Route path="subscriptions" element={<LearnerDashboardSubscriptions />} />
          <Route path="achievements" element={<LearnerDashboardAchievements />} />
        </Route>
        <Route path="overview/products" element={<ProductOverview />} />
        <Route path="overview/products/:tab" element={<ProductOverview />} />
        <Route path="mentor" element={<Qooper />} />
        <Route path="people" element={<People />} />
        <Route path="workshops" element={<DedicatedWorkshops />} />
        {/* <Route path="profile/:id" element={<Profile />} /> */}
        <Route path="profile" element={<LearnerProfile />} />
        <Route path="profile/:id" element={<LearnerProfile />} />
        {/* <Route path="settings" element={<ProfileSettings />} /> */}
        <Route path="meets-redirect" element={<MeetsRedirect />} />
        <Route path="teams-redirect" element={<TeamsRedirect />} />
        <Route path="google-redirect" element={<GoogleRedirect />} />
        {/* Targets of the API's OAuth relays (continu-api oauth-relay.ts RELAY_FRONTEND_PATHS). */}
        <Route path="zoom" element={<ZoomRedirect />} />
        <Route path="slack-redirect" element={<SlackRedirect />} />
        <Route path="slack-eddy-redirect" element={<SlackRedirect eddy />} />
        <Route path="stripe/success" element={<StripeSetupSuccess />} />
        <Route path="product/:id/payment-method" element={<PaymentWrapper context="create" />} />
        <Route path="product/:id/payment-setup-success" element={<PaymentSetupSuccess />} />
        <Route path="manage-subscription/:id" element={<SubscriptionManagement />} />
        <Route path="change-subscription/:id" element={<ChangeSubscription />} />
        <Route path="payment-history" element={<PaymentsList />} />;
        <Route
          path="subscription/:subscriptionId/update-payment"
          element={<PaymentWrapper context="update-existing" />}
        />
        <Route
          path="subscription/:subscriptionId/add-new-payment/:newProductId"
          element={<PaymentWrapper context="update-new" />}
        />
        <Route
          path="subscription/:id/payment-update-success"
          element={<PaymentMethodUpdateSuccess />}
        />
        <Route
          path="change-subscription/:id/product/:newProductId"
          element={<ChangeSubscription />}
        />
        <Route path="cancel-subscription/:subscriptionId" element={<CancelSubscription />} />
        <Route element={<DesktopOnlyLayout />}>
          <Route path="admin/integrations" element={<IntegrationsRoute />} />
          {/* Index/console half of the academies route family; the builder half
              is the admin/academies block under FullScreenAdminLayout above. */}
          <Route path="admin/academies" element={<AcademiesLayout />}>
            <Route index element={<AcademiesIndex />} />
            <Route path=":academyId" element={<AcademyConsoleLayout />}>
              <Route index element={<AcademyOverview />} />
              <Route path="branding" element={<BrandingSettings />} />
              <Route path="domain" element={<DomainSettings />} />
              <Route path="seo" element={<SeoSettings />} />
              <Route path="header-footer" element={<HeaderFooterSettings />} />
            </Route>
          </Route>
          <Route path="admin/badges" element={<BadgesLayout />}>
            <Route index element={<BadgesOverview />} />
            <Route path="edit/:badgeId?" element={<EditBadge />} />
          </Route>
          <Route path="admin/certificates" element={<CertificatesLayout />}>
            <Route index element={<CertificatesOverview />} />
            <Route path="create" element={<CertificateWizard />} />
            <Route path="edit/:certificateId" element={<CertificateWizard />} />
            <Route path="analytics/:certificateId" element={<CertificateAnalyticsPage />} />
            <Route path=":tab" element={<CertificatesOverview />} />
          </Route>

          <Route path="admin/workshops" element={<WorkshopsLayout />}>
            <Route index element={<WorkshopsOverview />} />
            <Route path=":tab" element={<WorkshopsOverview />} />
            <Route path="create" element={<WorkshopWizard />} />
            <Route path="edit/:id" element={<WorkshopWizard />} />
            <Route path="host/:id" element={<WorkshopHostView />} />
            <Route path="host/:id/session/:sessionId" element={<WorkshopSessionHostView />} />
            <Route path="host/:id/cohort/:cohortId" element={<WorkshopCohortHostView />} />
            <Route path="host/:id/instance/:instanceId" element={<WorkshopInstanceHostView />} />
          </Route>

          <Route path="admin/eddy" element={<AiAgentAdminLayout />}>
            <Route index element={<AiAgentAdminDashboard />} />

            <Route path="experts">
              <Route index element={<ExpertsOverview />} />
              <Route path="details/:expertId?" element={<ExpertDetails />} />
            </Route>
          </Route>

          <Route element={<PartnersPrincipalWrapper />}>
            <Route path="extend/manager" element={<PartnersManager />} />
            <Route path="extend/manager/content" element={<PartnerManagerContentList />} />
            <Route path="extend/manager/users" element={<PartnerUsersList />} />
            <Route path="extend/manager/users/create" element={<UserManagement />} />
            <Route path="extend/manager/users/:id" element={<UserManagement />} />
            <Route path="extend/manager/assignments" element={<PartnerManagerAssignmentsList />} />
            <Route
              path="extend/manager/content/:contentType/:id/assign"
              element={<PartnerManagerContentAssign />}
            />
            <Route
              path="extend/manager/content/:contentType/:id/activity"
              element={<PartnerManagerContentActivity />}
            />
          </Route>
          <Route element={<PartnersFeatureWrapper />}>
            <Route path="admin/extend" element={<PartnersList />} />
            <Route path="admin/extend/create" element={<PartnersEditor />} />
            <Route path="admin/extend/edit/:id" element={<PartnersEditor />} />
          </Route>

          <Route element={<AdminConnectLayout />}>
            <Route path="admin/connect/categories" element={<Categories />} />
            <Route path="admin/connect/locations" element={<Locations />} />
            <Route path="admin/connect/departments" element={<Departments />} />
            <Route path="admin/connect/teams" element={<Teams />} />
            <Route path="admin/connect/levels" element={<Levels />} />
            <Route path="admin/connect/grades" element={<Grades />} />
            <Route path="admin/connect/skills" element={<Skills />} />
            <Route path="admin/connect/groups" element={<Groups />} />
            <Route path="admin/connect/groups/create" element={<GroupForm />} />
            <Route path="admin/connect/groups/:groupId/edit" element={<GroupForm />} />
            <Route path="admin/connect/groups/:groupId/users" element={<GroupUsers />} />
          </Route>

          <Route element={<HubLayout />}>
            <Route path="admin/hubs" element={<Navigate to="/admin/hubs/organizations" replace />} />
            <Route path="admin/hubs/organizations" element={<HubOrganizations />} />
            <Route path="admin/hubs/content" element={<HubContentLibrary />} />
            <Route path="admin/hubs/reporting" element={<HubReporting />} />
            <Route path="admin/hubs/admins" element={<HubAdmins />} />
          </Route>

          <Route path="admin/edit/products" element={<ProductEditor />} />
          <Route path="admin/edit/products/:productId" element={<ProductEditor />} />

          <Route path="admin/theme" element={<Theme />} />

          <Route element={<AdminAllowanceLayout requires={['reports']} />}>
            <Route path="admin/reports" element={<Reports />} />
            <Route path="admin/reports/create" element={<ReportsForm />} />
            <Route path="admin/insights" element={<ContinuInsights />} />
            <Route path="admin/insights/unsubscribe" element={<ContinuInsightsUnsubscribe />} />
            <Route path="admin/insights/:dashboardId" element={<ContinuInsights />} />
            <Route path="admin/insights/:dashboardId/:vizId" element={<ContinuInsights />} />
            <Route path="admin/reports/:reportId" element={<ReportsForm />} />
          </Route>

          <Route element={<AdminAllowanceLayout requires={['profiles', 'settings']} />}>
            <Route path="admin/profiles" element={<Profiles />} />
            <Route path="admin/profiles/create" element={<ProfileForm />} />
            <Route path="admin/profiles/:profileId" element={<ProfileForm />} />
          </Route>

          <Route element={<AdminAllowanceLayout requires={['registrationForms']} />}>
            <Route path="admin/connect/registration-forms" element={<RegistrationFormsTable />} />
            <Route
              path="admin/connect/registration-forms/create"
              element={<RegistrationFormsEditor />}
            />
            <Route
              path="admin/connect/registration-forms/:id"
              element={<RegistrationFormsEditor />}
            />
          </Route>

          <Route element={<AdminAllowanceLayout requires={['productLandingPages']} />}>
            <Route path="admin/products/landing-page/create" element={<ProductLandingPage />} />
            <Route path="admin/products/landing-page/:id" element={<ProductLandingPage />} />
          </Route>

          <Route path="admin/users/custom-fields" element={<CompanyCustomFieldsEditor />} />
          <Route path="user-management" element={<UserManagement />} />
          <Route path="user-management/:id" element={<UserManagement />} />
          <Route path="admin/developer-tools" element={<DeveloperTools />} />
          <Route path="admin/developer-tools/:tab" element={<DeveloperTools />} />

          <Route element={<AdminContentAuthoringLayout />}>
            <Route path="admin/edit/article" element={<EditArticle />} />
            <Route path="admin/edit/article/:id" element={<EditArticle />} />

            <Route path="admin/edit/scorm" element={<EditScorm />} />
            <Route path="admin/edit/scorm/:id" element={<EditScorm />} />

            <Route path="admin/edit/file" element={<EditFile />} />
            <Route path="admin/edit/file/:id" element={<EditFile />} />

            <Route path="admin/edit/video" element={<EditVideo />} />
            <Route path="admin/edit/video/:id" element={<EditVideo />} />

            <Route path="admin/edit/assessment/:id?" element={<EditAssessment />} />
          </Route>

          <Route path="admin/users" element={<AdminUsersLayout />}>
            <Route index element={<AdminUserOverview />} />
            <Route path="add" element={<AdminUserAdd />} />
            <Route
              path="add/single"
              element={
                <UserManagement
                  showAdminPrimaryHeader={false}
                  renderUserDataTabs={false}
                  // Only this route is wrapped by AdminUsersLayout's matching manager gate.
                  allowManagerAddUser
                />
              }
            />
            <Route
              path="edit/single/:id"
              element={<UserManagement showAdminPrimaryHeader={false} />}
            />
            <Route path="add/bulk" element={<AdminUserBulkAdd />} />
            <Route path="seats" element={<UserSeats />} />
            <Route path="feeds" element={<SftpFeeds />} />
            <Route path="feeds/:id" element={<FeedActivity />} />
            <Route path="suspended/:id" element={<UserManagement />} />
          </Route>

          <Route path="admin/edit/course/:id?" element={<AdminCourseLayout />}>
            <Route index element={<TrackStart />} />

            <Route path="udemy" element={<EditCourse />} />
            <Route path="udemy/:id" element={<EditCourse />} />
            <Route path="linkedin" element={<EditCourse />} />
            <Route path="linkedin/:id" element={<EditCourse />} />
            <Route path="masterclass" element={<EditCourse />} />
            <Route path="masterclass/:id" element={<EditCourse />} />
            <Route path="custom" element={<EditCourse />} />
            <Route path="custom/:id" element={<EditCourse />} />
            <Route path="track" element={<EditCourse />} />
            <Route path="track/:id" element={<EditCourse />} />
          </Route>

          {/* DEV-13563: legacy Journeys redirect — drop once inbound traffic is gone. */}
          <Route path="admin/journeys" element={<Navigate to="/admin/content" replace />} />
          <Route path="admin/content" element={<ContentOverview />} />
          {/* Dedicated assessment analytics page — must precede the generic
              :contentType route so 'assessment' never reaches it. */}
          <Route
            path="admin/content/assessment/:contentId/analytics"
            element={<AssessmentAnalyticsPage />}
          />
          <Route
            path="admin/content/:contentType/:contentId/analytics"
            element={<ContentAnalyticsPage />}
          />
          <Route path="admin/edit/journey" element={<AdminJourneyLayout />}>
            <Route index element={<EditJourney />} />
            <Route path=":id" element={<EditJourney />} />
          </Route>

          <Route element={<SurveysLayout />}>
            <Route path="admin/surveys" element={<SurveysOverview />} />
            <Route path="admin/surveys/create" element={<EditSurvey />} />
            <Route path="admin/surveys/edit/:surveyId" element={<EditSurvey />} />
            <Route path="admin/surveys/host/:surveyId" element={<SurveyHostView />} />
          </Route>

          <Route path="admin/messaging" element={<Navigate to="overview" replace />} />
          <Route path="admin/messaging/overview" element={<AnnouncementsOverview />} />
          <Route path="admin/messaging/create" element={<EditAnnouncement />} />
          <Route path="admin/messaging/edit/:id" element={<EditAnnouncement />} />

          <Route path="admin/assignments" element={<AdminAssignmentsLayout />}>
            <Route index element={<Navigate to="overview" />} />

            <Route path="overview" element={<AssignmentsOverview />}>
              <Route path="all" element={<AssignmentsOverviewAllDirectAutomated />} />
              <Route path="direct" element={<AssignmentsOverviewAllDirectAutomated />} />
              <Route path="automated" element={<AssignmentsOverviewAllDirectAutomated />} />
              <Route path="userAssignments" element={<AssignmentsOverviewUserAssignments />} />
            </Route>

            <Route path="create" element={<EditAssignmentLayout />}>
              <Route index element={<EditAssignment />} />

              <Route path="instance" element={<EditAssignment />} />
            </Route>

            <Route path="edit/:id" element={<EditAssignmentLayout />}>
              <Route index element={<EditAssignment />} />
            </Route>

            <Route path="edit/instance/:id" element={<EditAssignmentLayout />}>
              <Route index element={<EditAssignment />} />
            </Route>

            <Route path="details/:id" element={<AssignmentDetails />}>
              <Route path="instance/:instanceId" element={<AssignmentDetails />} />
            </Route>
          </Route>
        </Route>
        <Route
          path="3229"
          element={
            <Suspense fallback={<div>Loading Icons...</div>}>
              <IconGallery />
            </Suspense>
          }
        />
        <Route path="article/:id" element={<ContentWrapper contentType="article" />} />
        <Route path="video/:id" element={<ContentWrapper contentType="video" />} />
        <Route path="file/:id" element={<ContentWrapper contentType="file" />} />
        <Route
          path="imported-content/:id"
          element={<ContentWrapper contentType="imported-content" />}
        />
        <Route path="scorm/:id" element={<ContentWrapper contentType="scorm" />} />
        {/* <Route
          path="article/:id"
          element={<ContentWrapper content={Article} contentType="article" />}
        /> */}
        {/* <Route
          path="file/:id"
          element={<ContentWrapper content={ContentFile} contentType="file" />}
        /> */}
        {/* <Route path="video/:id" element={<ContentWrapper content={Video} contentType="video" />} /> */}
        {/* <Route path="imported-content/:id" element={<ImportedContent />} /> */}
        {/* <Route path="scorm/:id" element={<ContentWrapper content={Scorm} contentType="scorm" />} /> */}
        <Route path="workshop/:id" element={<LearnerWorkshopRoute variant="single" />} />
        <Route
          path="multi-session-workshop/:id"
          element={<LearnerWorkshopRoute variant="multi" />}
        />
        {/* More specific path first: deep link to content within a track */}
        <Route
          path="track/:learningTrackId/:contentType/:id"
          element={<ContentWrapper contentType="track" />}
        />
        {/* Track overview */}
        <Route path="track/:learningTrackId" element={<ContentWrapper contentType="track" />} />
        <Route path="journey/:journeyId" element={<ContentWrapper contentType="journey" />} />
        <Route path="assessment/:id" element={<ContentWrapper contentType="assessment" />} />
        <Route path="product/:id" element={<Product />} />
        <Route path="attend-workshop/:id" element={<AutoAttendWorkshopWrapper />}>
          <Route index element={<AutoAttendWorkshop />} />
        </Route>
      </Route>
    </Route>
    </>,
  ),
);
